// SPDX-License-Identifier: AGPL-3.0-or-later

import {ResettableStates} from '@app/features/app/state/ResettableStates';
import type {FeedPollDraft} from '@app/features/feed/components/FeedPollEditor';
import {AccountScopedWork} from '@app/features/platform/state/AccountScopedWork';
import {makePersistent} from '@app/features/platform/utils/MobXPersistence';
import type {FeedTopicSort} from '@fluxer/schema/src/domains/feed/FeedTopicSchemas';
import {makeAutoObservable} from 'mobx';

// 'home' shows HOME_TIMELINE, the posts of the accounts you follow; Micup akis-v3: 'reels' the full-height video feed
// (FeedReels) in place of the list.
export type FeedTab = 'discover' | 'home' | 'reels';
// Micup akis-siralama: Discover's own list ('top') or its newest posts ('new'), inside the Discover tab: a value of
// FeedTab would send an older client's page to the home feed.
export type FeedDiscoverSort = 'top' | 'new';

// What the feed post box held when it went away (to a post page), for the account that wrote it.
export interface FeedComposerDraft {
	readonly userId: string | null;
	readonly text: string;
	readonly files: ReadonlyArray<File>;
	readonly poll: FeedPollDraft | null;
	readonly categoryId: string | null;
	readonly topicId: string | null;
}

class FeedTabs {
	selectedTab: FeedTab = 'discover';
	discoverSort: FeedDiscoverSort = 'top';
	// "Your posts" instead of the tabs, kept here (not persisted) so it is still chosen after a post page.
	showMine = false;
	// Micup akis-kategori: a category's posts in place of the tabs; for the session only, like showMine.
	selectedCategoryId: string | null = null;
	// Micup akis-gundem: a topic's page in place of the tabs, opening on its top posts; for the session only.
	selectedTopicId: string | null = null;
	topicSort: FeedTopicSort = 'top';
	// Not observed: the post box reads it once when it comes back. Not persisted (files and another account).
	composerDraft: FeedComposerDraft | null = null;

	constructor() {
		makeAutoObservable(this, {composerDraft: false}, {autoBind: true});
		void makePersistent(this, 'FeedTabs', ['selectedTab', 'discoverSort']);
	}

	reset(): void {
		this.showMine = false;
		this.selectedCategoryId = null;
		this.selectedTopicId = null;
		this.composerDraft = null;
	}

	setTab(tab: FeedTab): void {
		this.selectedTab = tab;
		this.selectedCategoryId = null;
		this.selectedTopicId = null;
	}

	setDiscoverSort(sort: FeedDiscoverSort): void {
		this.discoverSort = sort;
	}

	setShowMine(showMine: boolean): void {
		this.showMine = showMine;
		this.selectedCategoryId = null;
		this.selectedTopicId = null;
	}

	openCategory(categoryId: string): void {
		this.showMine = false;
		this.selectedCategoryId = categoryId;
		this.selectedTopicId = null;
	}

	closeCategory(): void {
		this.selectedCategoryId = null;
	}

	openTopic(topicId: string): void {
		this.showMine = false;
		this.selectedCategoryId = null;
		this.selectedTopicId = topicId;
		this.topicSort = 'top';
	}

	closeTopic(): void {
		this.selectedTopicId = null;
	}

	setTopicSort(sort: FeedTopicSort): void {
		this.topicSort = sort;
	}

	setComposerDraft(draft: FeedComposerDraft | null): void {
		this.composerDraft = draft;
	}
}

const feedTabs = new FeedTabs();
ResettableStates.register(feedTabs, feedTabs.reset);
AccountScopedWork.registerCancellation(() => feedTabs.reset());

export default feedTabs;
